import React, { useMemo } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { Group, ReportTemplate } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useGetScheduleHint } from './useGetScheduleHint';
import { numberToWeekday } from './weekdays';
import { numberToMonth } from './months';

interface ReportStatusIconsProps {
  groups: Group[];
  reportTemplate: ReportTemplate;
}

export const ReportStatusIcons = ({
  groups,
  reportTemplate,
}: ReportStatusIconsProps) => {
  const { messages } = useLocalization();
  const getScheduleHint = useGetScheduleHint();

  const isShared = groups.length > 0;
  const groupNames = groups.map((group) => group.name).join(', ');

  const isScheduled =
    reportTemplate.isScheduled &&
    reportTemplate.schedule?.interval !== 'disabled';

  const scheduleTooltip = useMemo(() => {
    if (!isScheduled) {
      return messages.reportsTemplateItemScheduleEmptyValue;
    }

    const interval = reportTemplate.schedule?.interval as string;
    let intervalStart = '';
    if (interval === 'weekly') {
      intervalStart = numberToWeekday(
        reportTemplate.schedule?.intervalStart as number
      );
    } else if (interval === 'yearly') {
      intervalStart = numberToMonth(
        reportTemplate.schedule?.intervalStart as number
      );
    }

    const hint = getScheduleHint(
      interval,
      intervalStart,
      reportTemplate.schedule?.endTime as Date,
      '00:00'
    );

    if (hint) {
      return `${hint.scheduleText} ${hint.nextJobText}`;
    }

    return '';
  }, [
    isScheduled,
    getScheduleHint,
    reportTemplate.schedule?.endTime,
    reportTemplate.schedule?.interval,
    reportTemplate.schedule?.intervalStart,
    messages.reportsTemplateItemScheduleEmptyValue,
  ]);

  // Only render if there's something to show - no space taken otherwise
  if (!isShared && !isScheduled) {
    return null;
  }

  // Dots stacked vertically with gap, only shown when active
  return (
    <div className="flex flex-col items-center justify-center gap-1 mr-1">
      {isShared && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="size-2 rounded-full bg-blue-500" />
          </TooltipTrigger>
          <TooltipContent>Shared with: {groupNames}</TooltipContent>
        </Tooltip>
      )}
      {isScheduled && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="size-2 rounded-full bg-green-500" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">{scheduleTooltip}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};
